// Governance alerts — process / hygiene issues that need a human action.
//
// Distinct from `scanRisks` (lib/insights.ts):
//   - scanRisks emits "this metric crossed a threshold or is trending bad"
//   - scanGovernanceAlerts emits "the data itself or the cadence around it
//     suggests a process break — close this Jira sprint, rename that one,
//     re-run the scoring flow, etc."
//
// Pure: no Vue, no fetch. Deterministic alert ids so the SPA can cache an
// LLM-generated explanation per alert (see composables/useExplainAlert.ts).

import type { SprintListItem, MetricEnvelope } from "@/api/types";
import { predictabilityRate, velocitySeries } from "@/lib/velocity";

export type AlertSeverity = "high" | "medium" | "low";
export type AlertCategory = "hygiene" | "process" | "data_quality";
export type AlertRule =
  | "overdue_active"
  | "duplicate_sprint_name"
  | "high_missing_estimates"
  | "rising_mid_sprint_additions"
  | "low_predictability";

export interface GovernanceAlert {
  /** Stable, deterministic. Format: "<projectKey>:<rule>:<scope>". */
  id: string;
  rule: AlertRule;
  severity: AlertSeverity;
  category: AlertCategory;
  title: string;
  body: string;
  /** Imperative action label, e.g. "Close sprint in Jira". */
  actionLabel: string;
  /** Structured payload an LLM-explain backend can ground on. */
  evidence: Record<string, unknown>;
}

const SEVERITY_RANK: Record<AlertSeverity, number> = { high: 0, medium: 1, low: 2 };
const day = 1000 * 60 * 60 * 24;

function readNumber(env: MetricEnvelope | undefined): number | null {
  if (!env || env.value == null) return null;
  return typeof env.value === "number" ? env.value : null;
}

function pct(v: number): string {
  return `${(v * 100).toFixed(0)}%`;
}

/**
 * Walk the sprint list and emit governance alerts. Deterministic order: by
 * severity, then by rule id.
 *
 * `now` is injectable for tests; defaults to current wall-clock.
 */
export function scanGovernanceAlerts(
  projectKey: string,
  sprints: SprintListItem[],
  now: Date = new Date(),
): GovernanceAlert[] {
  const out: GovernanceAlert[] = [];

  // Rule 1: overdue active sprint — Jira sprint still open past sprint_end.
  const active = sprints.find((s) => s.sprint_state === "active");
  if (active) {
    const end = Date.parse(active.sprint_end);
    if (!Number.isNaN(end) && now.getTime() > end) {
      const overdueDays = Math.round((now.getTime() - end) / day);
      out.push({
        id: `${projectKey}:overdue_active:${active.sprint_id}`,
        rule: "overdue_active",
        severity: overdueDays >= 3 ? "high" : "medium",
        category: "hygiene",
        title: "Active sprint is overdue",
        body: `${active.sprint_name} ended ${overdueDays} day${overdueDays === 1 ? "" : "s"} ago but is still marked active in Jira. Metrics will keep accruing mid-sprint additions and partial-completion noise until the sprint is closed.`,
        actionLabel: "Close sprint in Jira",
        evidence: {
          sprint_id: active.sprint_id,
          sprint_name: active.sprint_name,
          sprint_end: active.sprint_end,
          overdue_days: overdueDays,
        },
      });
    }
  }

  // Rule 2: duplicate sprint names within the project — Jira-side rename hygiene.
  const byName = new Map<string, SprintListItem[]>();
  for (const s of sprints) {
    const list = byName.get(s.sprint_name) ?? [];
    list.push(s);
    byName.set(s.sprint_name, list);
  }
  for (const [name, group] of byName) {
    if (group.length < 2) continue;
    const ids = group.map((g) => g.sprint_id).sort((a, b) => a - b);
    out.push({
      id: `${projectKey}:duplicate_sprint_name:${ids.join(",")}`,
      rule: "duplicate_sprint_name",
      severity: "medium",
      category: "hygiene",
      title: `${group.length} sprints share the same name`,
      body: `${group.length} sprints in ${projectKey} are named "${name}". Trend charts and narrative summaries lose meaning when sprint names aren't unique — rename them in Jira so each sprint can be told apart.`,
      actionLabel: "Rename duplicates in Jira",
      evidence: { sprint_name: name, sprint_ids: ids },
    });
  }

  // Rule 3: high missing-estimates rate — refinement-process break.
  const latestForEstimates = sprints[0];
  const m17 = readNumber(latestForEstimates?.headline_metrics?.M17);
  if (m17 != null && m17 >= 0.3) {
    const sev: AlertSeverity = m17 >= 0.5 ? "high" : "medium";
    out.push({
      id: `${projectKey}:high_missing_estimates:${latestForEstimates.sprint_id}`,
      rule: "high_missing_estimates",
      severity: sev,
      category: "process",
      title: `${pct(m17)} of issues are missing estimates`,
      body: `${pct(m17)} of issues in ${latestForEstimates.sprint_name} have no Story Points. Without estimates, M5 (planned SP), M7 (completion %), and predictability all degrade — sprint scoring becomes a guess.`,
      actionLabel: "Run a refinement session before next sprint start",
      evidence: {
        sprint_id: latestForEstimates.sprint_id,
        sprint_name: latestForEstimates.sprint_name,
        m17_value: m17,
      },
    });
  }

  // Rule 4: rising mid-sprint additions — three monotonically increasing M16.
  const lastThreeM16 = sprints
    .slice(0, 3)
    .map((s) => readNumber(s.headline_metrics?.M16));
  if (
    lastThreeM16.length === 3 &&
    lastThreeM16.every((v): v is number => v != null) &&
    lastThreeM16[0]! > lastThreeM16[1]! &&
    lastThreeM16[1]! > lastThreeM16[2]!
  ) {
    out.push({
      id: `${projectKey}:rising_mid_sprint_additions:${sprints[0].sprint_id}`,
      rule: "rising_mid_sprint_additions",
      severity: "medium",
      category: "process",
      title: "Mid-sprint additions trending up",
      body: `M16 has risen for 3 sprints in a row (${pct(lastThreeM16[2]!)} → ${pct(lastThreeM16[1]!)} → ${pct(lastThreeM16[0]!)}). Work is increasingly being added after sprint start, which suggests planning is shifting under the team mid-flight.`,
      actionLabel: "Re-tighten the planning ceremony",
      evidence: {
        m16_series_oldest_to_newest: [lastThreeM16[2], lastThreeM16[1], lastThreeM16[0]],
        sprint_ids: sprints.slice(0, 3).map((s) => s.sprint_id),
      },
    });
  }

  // Rule 5: low predictability across closed sprints — chronic over/under-delivery.
  const series = velocitySeries(sprints, 8);
  const pred = predictabilityRate(series);
  if (pred.qualifyingCount >= 3 && pred.rate != null && pred.rate < 0.5) {
    const rate = pred.rate;
    out.push({
      id: `${projectKey}:low_predictability:n=${pred.qualifyingCount}`,
      rule: "low_predictability",
      severity: rate < 0.3 ? "high" : "medium",
      category: "process",
      title: "Predictability under 50%",
      body: `Only ${pred.withinCount} of ${pred.qualifyingCount} recent sprints landed within ±10% of plan (${pct(rate)}). Either commitments are systematically off or scope keeps shifting; both undermine downstream roadmap planning.`,
      actionLabel: "Review sizing + commitment process with the team",
      evidence: {
        rate,
        within_count: pred.withinCount,
        qualifying_count: pred.qualifyingCount,
      },
    });
  }

  out.sort((a, b) => {
    const s = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    return s !== 0 ? s : a.rule.localeCompare(b.rule);
  });
  return out;
}
